import { useState } from 'react'
import { Search, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { ChannelInstanceResponse } from '@/services/channelsApi'
import OrderStatusTabs from './OrderStatusTabs'
import {
  FULFILLMENT_FILTERS,
  type FulfillmentFilterId,
  type OrderStatusTabId,
  type OrdersFilterState,
} from './orderFilters'

interface OrdersFiltersProps {
  filters: OrdersFilterState
  onChange: (next: OrdersFilterState) => void
  activeTab: OrderStatusTabId
  counts: Record<OrderStatusTabId, number | null>
  onTabChange: (tab: OrderStatusTabId) => void
  channelInstances: readonly ChannelInstanceResponse[]
  deliveryFilterEnabled: boolean
  fulfillmentFilter: FulfillmentFilterId
  onFulfillmentChange: (value: FulfillmentFilterId) => void
  activeFilterCount: number
  onClear: () => void
  searchPlaceholder: string
}

export default function OrdersFilters({
  filters,
  onChange,
  activeTab,
  counts,
  onTabChange,
  channelInstances,
  deliveryFilterEnabled,
  fulfillmentFilter,
  onFulfillmentChange,
  activeFilterCount,
  onClear,
  searchPlaceholder,
}: OrdersFiltersProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const advancedCount = (filters.from ? 1 : 0) + (filters.to ? 1 : 0) + (filters.channelInstanceId ? 1 : 0)

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
            placeholder={searchPlaceholder}
            aria-label="Buscar órdenes"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setAdvancedOpen((current) => !current)}
          aria-expanded={advancedOpen}
          aria-controls="orders-advanced-filters"
          className="min-h-[44px] shrink-0"
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filtros{advancedCount > 0 ? ` (${advancedCount})` : ''}
        </Button>
        {activeFilterCount > 0 ? (
          <Button type="button" variant="ghost" onClick={onClear} className="min-h-[44px] shrink-0">
            Limpiar
          </Button>
        ) : null}
      </div>

      <OrderStatusTabs activeTab={activeTab} counts={counts} onChange={onTabChange} />

      {advancedOpen ? (
        <div id="orders-advanced-filters" className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Desde
            <input
              type="date"
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800"
              value={filters.from}
              onChange={(event) => onChange({ ...filters, from: event.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Hasta
            <input
              type="date"
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800"
              value={filters.to}
              onChange={(event) => onChange({ ...filters, to: event.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Canal
            <select
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800"
              value={filters.channelInstanceId}
              onChange={(event) => onChange({ ...filters, channelInstanceId: event.target.value })}
            >
              <option value="">Todos</option>
              {channelInstances.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.instanceName} · {instance.channelType}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {deliveryFilterEnabled ? (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Tipo de entrega">
          {FULFILLMENT_FILTERS.map((option) => {
            const active = fulfillmentFilter === option.id
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={active}
                onClick={() => onFulfillmentChange(option.id)}
                className={[
                  'min-h-[36px] rounded-full px-3 text-xs font-semibold transition-colors',
                  active ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
